<script setup lang="ts">
import type { VButtonProps } from "./VButton.types";
import { computed, useAttrs } from "vue";
import ACTooltip from "../ACTooltip/ACTooltip.vue";
import VIcon from "../Icon/VIcon.vue";
import { buttonContainerCva, buttonCva, buttonIconCva, buttonLoaderCva } from "./VButton.cva";

// The button itself takes every attribute (data-testid, aria-*, listeners), so each lands on one element only. The
// class also sizes and places the wrappers around it, as it always did.
defineOptions({ inheritAttrs: false });

const {
  type = "button",
  variant = "primary",
  size = "medium",
  outlined = false,
  loading = false,
  iconLeft = undefined,
  hasPerms = true,
  iconRight = undefined,
  // Buttons are pills (house style); pass `:rounded="false"` only for a deliberate exception.
  rounded = true,
  disabled = false,
  iconOnly = false,
  textColor = undefined,
  static: isStatic = false,
  href = undefined,
} = defineProps<VButtonProps>();

const emit = defineEmits(["click"]);

const slots = defineSlots<{
  default: () => any
}>();

const attrs = useAttrs();

const isDisabled = computed(() => disabled || !hasPerms);

// Check if we have content in slots or if this is an icon-only button
const hasContent = computed(() => (slots.default && slots.default().length) && !iconOnly);

const isLink = computed(() => href !== undefined && !isDisabled.value);

/**
 * `textColor` wins over the text colour of every variant and of its hover and press states: several variants set their
 * text with `!important` (gray, light, input), which an inline colour without priority would lose to. The fill of
 * those states stays the variant's own, so pick a colour that reads on the variant's light hover fill. A CSS value
 * such as `var(--token)` works, so a caller can follow the theme. A disabled button keeps the variant's disabled look.
 */
const buttonStyle = computed(() => (textColor && !isDisabled.value ? { color: `${textColor} !important` } : undefined));

function handleClick(event: MouseEvent) {
  if (isDisabled.value || loading) {
    if (isLink.value) {
      event.preventDefault();
    }
    return;
  }
  emit("click", event);
}

// An icon-only button, set explicitly or because it has an icon but no text, is a round (circle) button.
const isIconOnly = computed(() => iconOnly || (!hasContent.value && (!!iconLeft || !!iconRight)));

// Compute classes using CVA
const classes = computed(() => {
  return {
    container: buttonContainerCva({
      loading,
      disabled: isDisabled.value,
      iconOnly: iconOnly || !hasContent.value,
    }),
    button: buttonCva({
      variant,
      size,
      outlined,
      rounded: rounded || isIconOnly.value,
      iconOnly: isIconOnly.value,
      loading,
      hasIconLeft: !!iconLeft,
      hasIconRight: !!iconRight,
      disabled: isDisabled.value,
      static: isStatic,
    }),
    iconLeft: buttonIconCva({
      position: "left",
      size,
      iconOnly: iconOnly || !hasContent.value,
    }),
    iconRight: buttonIconCva({
      position: "right",
      size,
      iconOnly: iconOnly || !hasContent.value,
    }),
    loader: buttonLoaderCva({
      size,
    }),
  };
});
</script>

<template>
  <ACTooltip
    type="button"
    :allow-change="hasPerms"
    :class="attrs.class"
  >
    <div
      :class="[
        classes.container,
        attrs.class,
      ]"
    >
      <component
        :is="isLink ? 'a' : 'button'"
        v-bind="attrs"
        :href="isLink ? href : undefined"
        :type="isLink ? undefined : type"
        :disabled="isLink ? undefined : isDisabled"
        :aria-busy="isLink && loading ? 'true' : undefined"
        :style="buttonStyle"
        :class="classes.button"
        @click="handleClick"
      >
        <span class="relative w-full flex items-center justify-center">
          <span :class="{ 'opacity-0': loading }" class="flex items-center justify-center">
            <VIcon
              v-if="iconLeft"
              :name="iconLeft"
              :class="classes.iconLeft"
            />
            <span
              v-if="hasContent && !iconOnly"
              class="overflow-hidden text-ellipsis whitespace-nowrap min-w-0"
            >
              <slot />
            </span>
            <VIcon
              v-if="iconRight"
              :name="iconRight"
              :class="classes.iconRight"
            />
          </span>
          <span v-if="loading" class="absolute inset-0 flex items-center justify-center" aria-hidden="true">
            <VIcon
              spin
              name="loader"
              :class="classes.loader"
            />
          </span>
        </span>
      </component>
    </div>
  </ACTooltip>
</template>
